import { useMemo } from 'react'
import {
  Badge,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core'

import { hasPermissions } from '@common/utils/validation/authValidators'
import { CommissionValvesPanel } from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/components/CommissionValvesPanel'
import { useValveCommissioning } from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/components/useValveCommissioning'
import { useValveRecoveryPolicy } from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/components/useValveRecoveryPolicy'
import { ValveAdvancedData } from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/components/ValveAdvancedData'
import { ValveRecoveryPanel } from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/components/ValveRecoveryPanel'
import { ValveStatsGrid } from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/components/ValveStatsGrid'
import {
  STATE_ICON,
  VALVE_STATE_META,
} from '@components/main/DeviceManagement/DeviceInformationManager/DeviceModalMantine/components/valveStateMeta'
import {
  useTestDeviceRefresh,
  type ValveTarget,
} from '@data/device/device'
import {
  type TestDevice,
  type Valve,
} from '@data/device/model/device'
import { type TokenPermission } from '@data/rbac/model/rbac'

export interface TestDeviceValvesTabProps {
  device: TestDevice
  permissions: TokenPermission[]
  deviceUpdating: boolean
}

// Test devices never have valve names or valve settings (manufacturer, size) — those belong
// to a provisioned installation — so each card shows the valve's status alone, and nothing on
// this tab reads or writes valve_settings. Everything else mirrors Device Management's Valves
// tab, through the same shared panels and hooks pointed at the /test_devices endpoints.
const ValveStatusCard = ({ valve }: { valve: Valve }) => {
  const meta = VALVE_STATE_META[valve.state ?? 7]
  const icon = STATE_ICON[valve.state ?? 7]

  return (
    <Card
      withBorder
      radius='md'
      p='lg'
    >
      <Group
        justify='space-between'
        wrap='nowrap'
      >
        <Group gap='sm'>
          <ThemeIcon
            variant='light'
            color={meta.color}
            size='lg'
            radius='xl'
          >
            {icon}
          </ThemeIcon>

          <Stack gap={0}>
            <Text fw={600}>
              {valve.key}
            </Text>

            <Text
              size='sm'
              c='dimmed'
            >
              Valve Status
            </Text>
          </Stack>
        </Group>

        <Badge
          color={meta.color}
          variant='light'
          leftSection={icon}
        >
          {meta.label}
        </Badge>
      </Group>
    </Card>
  )
}

export const TestDeviceValvesTab = ({
  device,
  permissions,
  deviceUpdating,
}: TestDeviceValvesTabProps) => {
  const valveTarget = useMemo<ValveTarget>(
    () => ({
      kind: 'test',
      deviceId: device.deviceId,
    }),
    [device.deviceId],
  )
  const valves = Object.values(device.valves)

  const { mutate: refreshDevice } = useTestDeviceRefresh()
  const {
    cooldown,
    isSending,
    handleCommissionValves,
  } = useValveCommissioning(valveTarget, () => refreshDevice(device.deviceId))

  const {
    selectedPolicy,
    isPolicyModified,
    isSavingPolicy,
    handlePolicyChange,
    handleDiscardPolicy,
    handleSavePolicy,
    probeConfirming,
    setProbeConfirming,
    handleProbe,
    isProbing,
    isFirmwareSupported,
    minFirmwareVersion,
  } = useValveRecoveryPolicy(valveTarget, device.valveRecovery, device.firmware)

  const hasValveCommPermission = hasPermissions(permissions, [
    'DEVICE:VALVE:COMM',
  ])

  const canCommissionValves =
    device.deviceVendor === 'SMARTFLOW' &&
    hasValveCommPermission

  return (
    <Stack gap='lg'>
      <ValveStatsGrid valves={valves} />

      <Card
        withBorder
        p='md'
      >
        <Stack gap='md'>
          <Group justify='space-between'>
            <Text fw={600}>
              Valves
            </Text>

            {hasValveCommPermission && (
              <ValveAdvancedData
                target={valveTarget}
                firmware={device.firmware}
              />
            )}
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2, xl: 3 }}>
            {valves.map((valve) => (
              <ValveStatusCard
                key={valve.key}
                valve={valve}
              />
            ))}
          </SimpleGrid>
        </Stack>
      </Card>

      {hasValveCommPermission && (
        <ValveRecoveryPanel
          selectedPolicy={selectedPolicy}
          isPolicyModified={isPolicyModified}
          isSavingPolicy={isSavingPolicy}
          onPolicyChange={handlePolicyChange}
          onDiscardPolicy={handleDiscardPolicy}
          onSavePolicy={handleSavePolicy}
          probeConfirming={probeConfirming}
          onProbeConfirmingChange={setProbeConfirming}
          onProbe={handleProbe}
          isProbing={isProbing}
          isFirmwareSupported={isFirmwareSupported}
          minFirmwareVersion={minFirmwareVersion}
        />
      )}

      {canCommissionValves && (
        <CommissionValvesPanel
          onCommission={handleCommissionValves}
          isSending={isSending}
          disabled={deviceUpdating || cooldown > 0}
          cooldown={cooldown}
        />
      )}
    </Stack>
  )
}
